import React, { Dispatch, FC, SetStateAction, useMemo, useReducer, useState } from 'react';
import { ChevronLeft } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { RowAlignCenter } from 'components/Basic/Basic.styles';
import { useLanguage } from 'context/AppContext';
import { SetDeviceResponse, SetTrayResponses } from 'api/Responses/SetResponses';
import { DeviceSearchResponse } from 'api/Responses/SearchResponses';
import DuplicateMergeModal from 'components/ConfirmModal/DuplicateMergeModal';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { partActions } from 'reducers/PartReducer/partActions';
import partReducer, { initialPart } from 'reducers/PartReducer/partReducer';
import { ModalContentType } from '../SetModal';
import { SetModalHeader, SetModalMedium } from '../SetModal.styles';
import AddPart from './AddPart';
import SearchPart from './SearchPart';
import SelectPartTray from './SelectPartTray';

// Type of content to be displayed in the modal
export type PartModalContentType = 'tray' | 'search' | 'add';

interface SelectPartProps {
  close: () => void;
  trays: string[];
  draftTrays: SetTrayResponses;
  dispatchDraft: Dispatch<SetAction>;
  setModalContent: Dispatch<SetStateAction<ModalContentType>>;
  initialTray?: string;
}

// Workflow to add an existing part to a set
const SelectPart: FC<SelectPartProps> = ({ close, trays, draftTrays, dispatchDraft, setModalContent, initialTray }) => {
  const [language] = useLanguage();
  const [partContent, setPartContent] = useState<PartModalContentType>(initialTray ? 'search' : 'tray');
  const [newPartTray, setNewPartTray] = useState<string | undefined>(initialTray);
  // Kept after closing so the confirm text stays stable while it fades out
  const [pending, setPending] = useState<SetDeviceResponse[]>([]);
  const [duplicateCodes, setDuplicateCodes] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Ticked search results in tick order; lives here so it survives opening a result's add form and coming back
  const [selectedParts, setSelectedParts] = useState<DeviceSearchResponse[]>([]);
  const [newPart, dispatchNewPart] = useReducer(partReducer, initialPart);

  // Determines what happens when the user presses the back button
  const backActions = useMemo(
    () => ({
      tray: () => setModalContent('selectItem'),
      search: () => {
        if (initialTray) return close();
        // Ticks were picked for this tray; don't carry them to a different tray choice
        setSelectedParts([]);
        setPartContent('tray');
      },
      add: () => setPartContent('search'),
    }),
    [setModalContent, initialTray, close],
  );

  const addParts = (parts: SetDeviceResponse[]): void => {
    if (!newPartTray) return;
    close();
    setSelectedParts([]);
    parts.forEach(part => dispatchDraft(draftActions.partNew(part, newPartTray)));
    setTimeout(() => setModalContent('selectItem'), 500);
  };

  // Codes already in the tray need the user to confirm a merge first
  const addSelected = (results: DeviceSearchResponse[]): void => {
    const trayParts = (newPartTray && draftTrays[newPartTray]?.parts) || {};
    // New devices get the next refs after the tray's highest numeric ref; merged ones keep the existing row's ref
    // Only whole-number refs count, so '2.5' or '1e3' can't push the numbering off
    const refNumber = (ref: string): number => (/^\d+$/.test(ref?.trim() ?? '') ? Number(ref) : 0);
    let nextRef = Math.max(0, ...Object.values(trayParts).map(p => refNumber(p.ref))) + 1;
    const parts = results.map(r => {
      const part = partReducer(initialPart, partActions.fromSearch(r));
      return trayParts[part.code] ? part : { ...part, ref: String(nextRef++) };
    });
    const codes = parts.map(p => p.code).filter(code => trayParts[code]);
    setPending(parts);
    setDuplicateCodes(codes);
    if (codes.length) setConfirmOpen(true);
    else addParts(parts);
  };

  const toggleSelected = (part: DeviceSearchResponse): void =>
    setSelectedParts(prev =>
      prev.some(p => p.code === part.code) ? prev.filter(p => p.code !== part.code) : [...prev, part],
    );

  // Props passed to workflows
  const workflowProps = {
    tray: { trays, setNewPartTray, setPartContent },
    search: {
      dispatchNewPart,
      setPartContent,
      onAddSelected: addSelected,
      selectedParts,
      onToggleSelected: toggleSelected,
      onClearSelected: () => setSelectedParts([]),
    },
    add: { close, newPart, newPartTray, draftTrays, dispatchNewPart, dispatchDraft, setModalContent },
  };

  return (
    <SetModalMedium>
      <RowAlignCenter>
        <IconButton
          sx={{ position: 'absolute !important', top: '1rem', left: '1rem' }}
          onClick={backActions[partContent]}
        >
          <ChevronLeft />
        </IconButton>
        <SetModalHeader>{language.AddModal.Device.headers[partContent]}</SetModalHeader>
      </RowAlignCenter>
      {
        {
          tray: <SelectPartTray {...workflowProps.tray} />,
          search: <SearchPart {...workflowProps.search} />,
          add: <AddPart {...workflowProps.add} />,
        }[partContent]
      }
      <DuplicateMergeModal
        open={confirmOpen}
        close={() => setConfirmOpen(false)}
        confirm={() => addParts(pending)}
        tray={newPartTray ?? ''}
        codes={duplicateCodes}
      />
    </SetModalMedium>
  );
};
export default SelectPart;
